import { useState, useMemo, useRef, useEffect } from 'react';
import assets from '../../assets';
import { useApi } from '../../hooks/use-api';
import { useNotify } from '../../hooks/use-notify';
import { 
    API_ADMIN_ACCESS_REQUESTS_STATS, 
    API_ADMIN_ACCESS_REQUESTS, 
    API_ADMIN_UPDATE_ACCESS_REQUEST,
    API_ADMIN_BULK_UPDATE_ACCESS_REQUESTS,
    API_ADMIN_ACCESS_REQUESTS_EXAMS
} from '../../utils/api-routes';

export const useAdminCompetitiveExam = () => {
    const { get, post } = useApi();
    const { toggleNotify } = useNotify();
    const fileInputRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [selectedExam, setSelectedExam] = useState('All Exams');
    const [selectedStatus, setSelectedStatus] = useState('All Status');
    const [selectedStudents, setSelectedStudents] = useState({});
    const [studentRequests, setStudentRequests] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        accepted: 0,
        rejected: 0
    });
    const [totalResults, setTotalResults] = useState(0);
    const [examOptions, setExamOptions] = useState([]);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const response = await get(API_ADMIN_ACCESS_REQUESTS_STATS);
            if (response.data) {
                // Mapping backend response to our stats object
                // Backend might return { total: X, pending: Y, ... } or { data: { ... } }
                const data = response.data.data || response.data;
                setStats({
                    total: data.total || 0,
                    pending: data.pending || 0,
                    accepted: data.approved || data.accepted || 0,
                    rejected: data.rejected || data.declined || 0
                });
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
            toggleNotify({
                type: 'error',
                title: 'Error',
                description: 'Failed to fetch statistics.'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchExams = async () => {
        try {
            const response = await get(API_ADMIN_ACCESS_REQUESTS_EXAMS);
            if (response.data) {
                const data = response.data.data || response.data;
                const normalizedExams = Array.isArray(data) 
                    ? data.map(item => {
                        if (typeof item === 'string') return item;
                        return item.exam || item.examName || item.exam_name || item.name;
                    }).filter(Boolean)
                    : [];
                setExamOptions(normalizedExams);
            }
        } catch (error) {
            console.error("Error fetching exams:", error);
        }
    };

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: pageSize
            };
            
            // Only add filters if they are not the default "All" values
            if (search) params.search = search;
            if (selectedExam && selectedExam !== 'All Exams') params.exam = selectedExam;
            if (selectedStatus && selectedStatus !== 'All Status') params.status = selectedStatus;

            const response = await get(API_ADMIN_ACCESS_REQUESTS, { params });
            if (response.data) {
                // The API might return the list directly or wrapped in an object
                const data = Array.isArray(response.data) ? response.data : (response.data.requests || response.data.data || []);
                setStudentRequests(data.map(item => ({
                    ...item,
                    id: item.id || item._id,
                    name: item.name || (item.user?.name) || (item.firstName ? `${item.firstName} ${item.lastName || ''}` : item.email || 'Unknown Student'),
                    email: item.email || item.user?.email || item.mail,
                    exam: item.exam || item.examType || item.course_name,
                    requestDate: (() => {
                        const dateVal = item.requestDate || item.createdAt || item.created_at;
                        if (!dateVal) return '---';
                        const d = new Date(dateVal);
                        const day = String(d.getDate()).padStart(2, '0');
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const year = d.getFullYear();
                        let hours = d.getHours();
                        const minutes = String(d.getMinutes()).padStart(2, '0');
                        const ampm = hours >= 12 ? 'PM' : 'AM';
                        hours = hours % 12;
                        hours = hours ? hours : 12; // the hour '0' should be '12'
                        const strHours = String(hours).padStart(2, '0');
                        return `${day}/${month}/${year} ${strHours}:${minutes} ${ampm}`;
                    })(),
                    status: item.status || 'Pending',
                    avatar: item.avatar || item.user?.avatar || assets.ic_chat_person,
                    utr_id: item.utr_id || item.transaction_id || 'N/A',
                    screenshot_url: item.screenshot_url || item.screenshot || item.payment_proof
                })));
                
                // Update total count if provided by API, otherwise use list length
                setTotalResults(response.data.total || response.data.totalCount || data.length);
            }
        } catch (error) {
            console.error("Error fetching requests:", error);
            toggleNotify({
                type: 'error',
                title: 'Error',
                description: 'Failed to fetch access requests.'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [search, selectedExam, selectedStatus]);

    useEffect(() => {
        fetchRequests();
    }, [currentPage, pageSize, search, selectedExam, selectedStatus]);

    useEffect(() => {
        fetchStats();
        fetchExams();
    }, []);

    const refreshData = () => {
        fetchStats();
        fetchRequests();
    };

    // Filtering is now handled on the server side via query parameters

    // Stats are now fetched from API instead of being calculated locally
    // but we can keep a local calculation as fallback if needed
    /*
    const calculatedStats = useMemo(() => {
        return {
            total: studentRequests.length,
            pending: studentRequests.filter(s => s.status === 'Pending').length,
            accepted: studentRequests.filter(s => s.status === 'Approved').length,
            rejected: studentRequests.filter(s => s.status === 'Declined').length
        };
    }, [studentRequests]);
    */

    const handleCheckboxChange = (id) => {
        setSelectedStudents(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allSelected = {};
            studentRequests.forEach(item => {
                allSelected[item.id] = true;
            });
            setSelectedStudents(allSelected);
        } else {
            setSelectedStudents({});
        }
    };

    const handleAction = async (ids, action) => {
        try {
            if (ids.length >= 1) {
                // Use bulk update endpoint for both single and multiple requests as it is more versatile
                await post(API_ADMIN_BULK_UPDATE_ACCESS_REQUESTS, {
                    request_ids: ids,
                    action: action,
                    admin_note: ""
                });
            }
            
            toggleNotify({
                type: 'success',
                title: 'Success',
                description: `Successfully processed the request(s) as ${action}.`
            });
            
            refreshData();
            setSelectedStudents({});
        } catch (error) {
            console.error(`Error performing ${action}:`, error);
            toggleNotify({
                type: 'error',
                title: 'Action Failed',
                description: error.response?.data?.message || `Failed to ${action} the request.`
            });
        }
    };

    const approveSelected = () => {
        const selectedIds = Object.keys(selectedStudents).filter(id => selectedStudents[id]);
        if (selectedIds.length > 0) {
            handleAction(selectedIds, 'APPROVE');
        }
    };

    const rejectSelected = () => {
        const selectedIds = Object.keys(selectedStudents).filter(id => selectedStudents[id]);
        if (selectedIds.length > 0) {
            handleAction(selectedIds, 'REJECT');
        }
    };

    const resubmitSelected = () => {
        const selectedIds = Object.keys(selectedStudents).filter(id => selectedStudents[id]);
        if (selectedIds.length > 0) {
            handleAction(selectedIds, 'RESUBMIT');
        }
    };

    const approveOne = (id) => {
        handleAction([id.toString()], 'APPROVE');
    };

    const rejectOne = (id) => {
        handleAction([id.toString()], 'REJECT');
    };

    const resubmitOne = (id) => {
        handleAction([id.toString()], 'RESUBMIT');
    };

    const clearSelection = () => {
        setSelectedStudents({});
    };

    const openDetailModal = (request) => {
        setSelectedRequest(request);
        setShowDetailModal(true);
    };

    const closeDetailModal = () => {
        setShowDetailModal(false);
        setSelectedRequest(null);
    };

    return {
        studentRequests,
        stats,
        search,
        setSearch,
        selectedExam,
        setSelectedExam,
        selectedStatus,
        setSelectedStatus,
        selectedStudents,
        handleCheckboxChange,
        handleSelectAll,
        approveSelected,
        rejectSelected,
        resubmitSelected,
        approveOne,
        rejectOne,
        resubmitOne,
        clearSelection,
        currentPage,
        setCurrentPage,
        pageSize,
        refreshData,
        totalResults,
        examOptions,
        showDetailModal,
        selectedRequest,
        openDetailModal,
        closeDetailModal,
        loading
    };
};
